import {IconButton, useDisclosure} from '@tempo/core';
import React from 'react';
import BottomSheet from '@components/BottomSheet';
import {HEADER_SIZE} from '@components/BottomSheet/Header';
import {
	MoreHorizontalIcon,
	PenIcon,
	LogoutIcon,
	HumanPlusIcon,
} from '@tempo/icons';
import ModifyGroupModal from '../Modal/ModifyGroupModal';
import LeaveGroupModal, {
	type GroupSelected,
} from '@components/Modal/LeaveGroupModal';
import {useTranslation} from 'next-i18next';
import {useInvitationLink} from '@providers/InvitationLinkContext';
import useDzrUniqId from '@hooks/useDzrUniqId';
import {useLogEvent} from '@providers/LogEventProvider';
import {useUser} from '@providers/UserProvider';

type GroupActionsProps = {
	groupData: GroupSelected;
};

const GroupActions: React.FC<GroupActionsProps> = ({groupData}) => {
	const {t} = useTranslation();
	const {onOpenBottomSheetShare} = useInvitationLink();

	const {user} = useUser();
	const {eventLogs} = useLogEvent();
	const dzrUniqId = useDzrUniqId();

	const {
		isOpen: isBottomSheetOpen,
		onOpen: onOpenBottomSheet,
		onClose: onCloseBottomSheet,
	} = useDisclosure();

	const {
		isOpen: isModalModifyOpen,
		onOpen: onOpenModifyModal,
		onClose: onCloseModifyModal,
	} = useDisclosure();
	const {
		isOpen: isModalLeaveOpen,
		onOpen: onOpenLeaveModal,
		onClose: onCloseLeaveModal,
	} = useDisclosure();

	React.useEffect(() => {
		if (user && groupData && isBottomSheetOpen) {
			eventLogs?.logScreenViewGroupSetting({
				userId: user.id,
				groupId: groupData.id as string,
			});
		}
	}, [user, dzrUniqId, eventLogs, isBottomSheetOpen, groupData]);

	return (
		<>
			<IconButton
				icon={<MoreHorizontalIcon color="icon.neutral.primary.default" />}
				aria-label="group menu"
				variant="ghost"
				onClick={(e) => {
					e.stopPropagation();
					onOpenBottomSheet();
				}}
			/>
			<BottomSheet isOpen={isBottomSheetOpen} onClose={onCloseBottomSheet}>
				<BottomSheet.Header size={HEADER_SIZE.S}>
					<BottomSheet.Header.Title>{groupData.name}</BottomSheet.Header.Title>
				</BottomSheet.Header>

				<BottomSheet.Section>
					<BottomSheet.Button
						icon={<HumanPlusIcon />}
						onClick={() => {
							onOpenBottomSheetShare(groupData.id);
							onCloseBottomSheet();
						}}
					>
						{t('musictogether_action_invitefriends_web')}
					</BottomSheet.Button>
					<BottomSheet.Button
						icon={<PenIcon />}
						onClick={() => {
							onCloseBottomSheet();
							onOpenModifyModal();
						}}
					>
						{t('musictogethergroupnameedit_title_editgroupname_web')}
					</BottomSheet.Button>
					<BottomSheet.Button
						icon={<LogoutIcon />}
						onClick={() => {
							onCloseBottomSheet();
							onOpenLeaveModal();
						}}
					>
						{t('musictogether_action_leavethegroup_web')}
					</BottomSheet.Button>
				</BottomSheet.Section>
			</BottomSheet>
			<ModifyGroupModal
				isOpen={isModalModifyOpen}
				onClose={onCloseModifyModal}
				groupSelected={groupData}
			/>
			<LeaveGroupModal
				isOpen={isModalLeaveOpen}
				onClose={onCloseLeaveModal}
				groupSelected={groupData}
			/>
		</>
	);
};

export default GroupActions;
